import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { formatYMDRange, formatTimeRange } from "@/lib/dates";

const TYPE_LABEL = {
  retreat: "Retreat",
  training: "Teacher Training",
  course: "Online Course",
  event: "Gathering",
};

export default function OfferingCard({ offering, index = 0 }) {
  // Smart-route: Solstice/Saddle&Soul each have their own dedicated landing pages;
  // the Aerial Yoga Teacher Training has a master /trainings/aerial-yoga landing.
  // Everything else opens in the generic /offering/<slug> detail page.
  const slug = (offering.slug || "").toLowerCase();
  let detailPath = `/offering/${offering.slug}`;
  if (slug.includes("solstice-in-the-saddle")) detailPath = "/retreats/solstice-in-the-saddle";
  else if (slug.includes("winter-solstice-retreat")) detailPath = "/retreats/winter-solstice-retreat";
  else if (slug.includes("aerial-yoga-teacher-training") && offering.type === "training") detailPath = "/trainings/aerial-yoga";

  const typeLabel = TYPE_LABEL[offering.type] || offering.type;
  const spotsLeft = offering.spots_left;
  const showUrgency = spotsLeft <= 5 && spotsLeft < 9999 && spotsLeft > 0;
  const soldOut = spotsLeft === 0;

  return (
    <Link
      to={detailPath}
      onClick={() => trackEvent("offering_card_click", {
        offering_slug: offering.slug,
        offering_title: offering.title,
        offering_type: offering.type,
        spots_left: offering.spots_left,
        price_usd: offering.price_usd,
        destination_path: detailPath,
        source: "offering_card",
      })}
      className="group block relative"
      data-testid={`offering-card-${offering.slug}`}
    >
      {/* Image with type badge + optional spot urgency overlay */}
      <div className="relative img-zoom rounded-[2rem] aspect-[4/5] mb-6 ring-1 ring-aviana-border overflow-hidden">
        <img src={offering.image_url} alt={offering.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        {/* Soft gradient overlay at bottom for legibility */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-aviana-ink/45 to-transparent pointer-events-none" />
        {/* Type badge — top-left */}
        <div className="absolute top-4 left-4">
          <span className="inline-block px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-aviana-cream bg-aviana-plum/85 backdrop-blur-sm rounded-full font-medium">
            {typeLabel}
          </span>
        </div>
        {/* Urgency / sold-out badge — top-right */}
        {soldOut && (
          <div className="absolute top-4 right-4">
            <span className="inline-block px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-aviana-ink bg-aviana-cream/95 rounded-full">Sold Out</span>
          </div>
        )}
        {showUrgency && (
          <div className="absolute top-4 right-4">
            <span className="inline-block px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase text-aviana-cream bg-aviana-gold/90 rounded-full font-medium" data-testid={`urgency-${offering.slug}`}>
              {spotsLeft === 1 ? "1 spot left" : `${spotsLeft} spots left`}
            </span>
          </div>
        )}
        {/* Date label — bottom-left on image */}
        {offering.start_date && (
          <div className="absolute bottom-4 left-4 text-aviana-cream">
            <p className="font-display text-[15px] leading-tight drop-shadow-sm">
              {formatYMDRange(offering.start_date, offering.end_date) || "Self-paced · Begin anytime"}
            </p>
            {formatTimeRange(offering.start_time, offering.end_time) && (
              <p className="mt-0.5 text-[12px] tracking-[0.14em] uppercase text-aviana-cream/85 drop-shadow-sm" data-testid={`card-time-${offering.slug}`}>
                {formatTimeRange(offering.start_time, offering.end_time)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="font-serif text-[28px] md:text-[30px] leading-[1.08] text-aviana-ink mb-3 group-hover:text-aviana-plum transition-colors">
        {offering.title}
      </h3>

      {/* Subtitle */}
      {offering.subtitle && (
        <p className="text-[14px] text-aviana-muted mb-5 italic font-serif leading-[1.5]">{offering.subtitle}</p>
      )}

      {offering.exclusive_to_studio && (
        <p className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-aviana-plum font-medium mb-4" data-testid={`offering-exclusive-${offering.slug}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-aviana-plum" />
          Exclusive · {offering.exclusive_studio_name || "Studio instructors only"}
        </p>
      )}

      {/* Meta row — location only (date is now on the image) */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[12px] text-aviana-muted/85 mb-6">
        <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-aviana-gold" />{offering.location}</span>
        {!offering.start_date && (
          <span className="inline-flex items-center gap-1.5"><Calendar size={13} className="text-aviana-gold" />Self-paced</span>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-aviana-border/70 mb-5" />

      {/* Price + CTA */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-1 !text-aviana-muted/70">From</p>
          <p className="font-figure text-[26px] leading-none text-aviana-ink">${(typeof offering.price_usd === "number" ? offering.price_usd : 0).toLocaleString()}</p>
          {typeof offering.sisterhood_price_usd === "number" && (
            <p className="text-[11px] tracking-[0.18em] uppercase text-aviana-goldDeep mt-2" data-testid={`card-sisterhood-${offering.slug}`}>
              or ${offering.sisterhood_price_usd.toLocaleString()} sisterhood
            </p>
          )}
          {typeof offering.deposit_usd === "number" && offering.deposit_usd > 0 && (
            <p className="text-[11px] text-aviana-muted mt-1">${offering.deposit_usd} deposit holds your spot</p>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.22em] uppercase text-aviana-plum group-hover:text-aviana-goldDeep group-hover:gap-2 transition-all whitespace-nowrap pb-1">
          Discover
          <ArrowUpRight size={14} className="transition-transform group-hover:rotate-12" />
        </span>
      </div>
    </Link>
  );
}
